import { Container } from '@mui/material';
import classNames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatManwon } from '../../utils/formatKRW';
import { logEvent } from '../../utils/ga';
import { ExpertCallout } from '../shared/ExpertCallout';
import { FireForm } from './Form/FireForm';
import { calculateFire, FireFormValues, FireResult, getFireCallout } from './FirePage.hooks';
import styles from './FirePage.module.css';

function FirePage() {
  const resultRef = useRef<HTMLDivElement>(null);
  const [result, setResult] = useState<FireResult>();
  const [values, setValues] = useState<FireFormValues>();

  const handleSubmit = useCallback((nextValues: FireFormValues) => {
    logEvent('calculator', 'fire_calculate', 'submit');
    setValues(nextValues);
    setResult(calculateFire(nextValues));
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, []);

  const callout = useMemo(() => (result ? getFireCallout(result) : null), [result]);
  const achievementPoint = useMemo(
    () => result?.yearlyData.find((item) => item.totalAssets >= item.fireNumber),
    [result],
  );

  const heroClassName = useMemo(() => {
    if (!result || result.yearsToFire > 20 || result.yearsToFire < 0) {
      return styles.heroWarning;
    }

    if (result.yearsToFire <= 10) {
      return styles.heroSuccess;
    }

    return styles.heroNeutral;
  }, [result]);

  return (
    <div className={styles.root}>
      <Helmet>
        <title>FIRE 계산기 — 조기 은퇴 목표 자산·가능 나이 계산 | 노후든든</title>
        <meta
          name="description"
          content="FIRE(경제적 자립·조기은퇴) 목표 자산을 계산하세요. 월 지출, 투자 수익률, 현재 자산을 입력하면 FIRE 달성 나이와 필요 납입액을 자동으로 계산합니다."
        />
      </Helmet>

      <Container maxWidth="sm">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>FIRE 계산기</h1>
          <p className={styles.pageDesc}>
            월 생활비와 투자 계획을 기준으로 목표 자산, 달성 예상 나이, 필요한 월 투자액을 한 번에 계산합니다.
          </p>
        </div>
        <div className={styles.formCard}>
          <FireForm onSubmit={handleSubmit} />
        </div>
      </Container>

      {result && values && (
        <div ref={resultRef}>
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <div className={styles.resultSection}>
              <div className={classNames(styles.heroCard, heroClassName)}>
                <p className={styles.heroLabel}>FIRE 달성 나이</p>
                <p className={styles.heroAmount}>
                  {result.yearsToFire < 0 ? '달성 어려움' : `${result.fireAge}세`}
                </p>
                <p className={styles.heroSub}>
                  현재 {values.currentAge}세 기준 {result.yearsToFire < 0 ? '60년 내 달성 불가' : `${result.yearsToFire}년 후`} · 목표 자산 {formatManwon(result.fireNumber)}
                </p>
              </div>

              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>FIRE 목표 자산</p>
                  <p className={styles.metricValue}>{formatManwon(result.fireNumber)}</p>
                  <p className={styles.metricInterpretation}>연간 지출의 25배 기준</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>현재 달성률</p>
                  <p className={styles.metricValue}>{result.yearlyData[0].progressPct.toFixed(1)}%</p>
                  <p className={styles.metricInterpretation}>현재 자산 기준 목표 대비 비율</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>월 필요 투자액</p>
                  <p className={styles.metricValue}>{formatManwon(result.monthlyRequired)}</p>
                  <p className={styles.metricInterpretation}>현재 자산을 바탕으로 목표 달성에 필요한 최소치</p>
                </div>
              </div>

              <div className={styles.chartCard}>
                <p className={styles.chartTitle}>예상 자산 증가 곡선</p>
                <div className={styles.chartWrapper}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={result.yearlyData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="fireArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="age" tickLine={false} axisLine={false} />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `${(value / 100_000_000).toFixed(0)}억`}
                      />
                      <Tooltip
                        formatter={((value: number, name: string) => [
                          formatManwon(value),
                          name === 'totalAssets' ? '예상 자산' : 'FIRE 목표선',
                        ]) as any}
                      />
                      <Area
                        type="monotone"
                        dataKey="totalAssets"
                        stroke="#2563eb"
                        fillOpacity={1}
                        fill="url(#fireArea)"
                        strokeWidth={2.5}
                      />
                      <Line
                        type="monotone"
                        dataKey="fireNumber"
                        stroke="#ef4444"
                        strokeWidth={2}
                        strokeDasharray="6 6"
                        dot={false}
                      />
                      {achievementPoint && (
                        <ReferenceDot
                          x={achievementPoint.age}
                          y={achievementPoint.totalAssets}
                          r={6}
                          fill="#ef4444"
                          stroke="#fff"
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {callout && (
                <ExpertCallout title={callout.title} body={callout.body} cta={callout.cta} />
              )}

              <p className={styles.disclaimer}>
                4% 인출 규칙은 미국 주식시장 30년 역사 기반 William Bengen 연구에서 유래합니다. 한국 시장 환경, 세금, 인플레이션에 따라 실제 안전 인출률은 다를 수 있습니다.
              </p>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
}

export default FirePage;
